import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import * as mammoth from "mammoth";
import DOMPurify from "dompurify";
import { ButtonShadcn } from "@/components/ui/Button";

const PreviewModal = ({ file, isOpen, onClose }) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState("");
  const [docxHtml, setDocxHtml] = useState(null);
  const [content, setContent] = useState("");
  const [emlData, setEmlData] = useState(null); // For parsed EML data
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && file) {
      document.body.classList.add("blurred");
      loadFileForPreview();
    } else {
      cleanup();
      document.body.classList.remove("blurred");
    }

    return () => {
      cleanup();
      document.body.classList.remove("blurred");
    };
  }, [isOpen, file]);

  const cleanup = () => {
    if (fileUrl && fileUrl.startsWith("blob:") && (!file || fileUrl !== file.objectUrl)) {
      URL.revokeObjectURL(fileUrl);
    }
    setFileUrl(null);
    setDocxHtml(null);
    setContent("");
    setEmlData(null);
    setError(null);
    setLoading(false);
    setFileType("");
  };

  const getFileExtension = () => {
    if (!file || !file.filename) return "";
    const parts = file.filename.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
  };

  const loadFileForPreview = async () => {
    setLoading(true);
    setError(null);

    const extension = getFileExtension();
    setFileType(extension);

    try {

      // Handle different file types
      switch (extension) {
        case "pdf":
          await loadPdfFile();
          break;

        case "docx":
        case "doc":
          await loadDocxFile();
          break;

        case "eml":
          await loadEmlFile();
          break;

        case "txt":
        case "json":
        case "js":
        case "jsx":
        case "ts":
        case "tsx":
        case "css":
        case "html":
        case "xml":
        case "md":
          loadTextFile();
          break;

        default:
          loadOtherFile();
      }
    } catch (error) {
      console.error("Error loading file for preview:", error);
      setError(`Cannot display file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPdfFile = async () => {
    let url = null;

    if (file.objectUrl && file.objectUrl.startsWith("blob:")) {
      url = file.objectUrl;
    } else if (file.originalFile && file.originalFile instanceof File) {
      url = URL.createObjectURL(file.originalFile);
    } else if (file.preview) {
      try {
        const blob = new Blob([file.preview], { type: "application/pdf" });
        url = URL.createObjectURL(blob);
      } catch (e) {
        throw new Error("Cannot create PDF from preview data");
      }
    } else {
      throw new Error("No PDF data available");
    }

    setFileUrl(url);
  };

  const loadDocxFile = async () => {
    try {
      let arrayBuffer = null;

      if (file.originalFile && file.originalFile instanceof File) {
        arrayBuffer = await file.originalFile.arrayBuffer();
      } else if (file.objectUrl) {
        const response = await fetch(file.objectUrl);
        arrayBuffer = await response.arrayBuffer();
      } else if (file.preview && file.preview instanceof ArrayBuffer) {
        arrayBuffer = file.preview;
      } else if (file.preview && typeof file.preview === "string") {
        const encoder = new TextEncoder();
        arrayBuffer = encoder.encode(file.preview).buffer;
      } else {
        throw new Error("No DOCX data available for preview");
      }

      const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
      let htmlContent = result.value;
      const messages = result.messages;

      if (messages.length > 0) {
      }

      htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <style>
                        body {
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            line-height: 1.6;
                            color: #333;
                            max-width: 210mm;
                            margin: 0 auto;
                            padding: 20px;
                            background: white;
                        }
                        h1, h2, h3, h4, h5, h6 {
                            color: #1a1a1a;
                            margin-top: 1.5em;
                            margin-bottom: 0.5em;
                            font-weight: 600;
                        }
                        h1 { font-size: 2em; }
                        h2 { font-size: 1.5em; }
                        h3 { font-size: 1.17em; }
                        p {
                            margin: 0.5em 0;
                            text-align: justify;
                        }
                        ul, ol {
                            margin: 0.5em 0;
                            padding-left: 2em;
                        }
                        li {
                            margin: 0.25em 0;
                        }
                        table {
                            border-collapse: collapse;
                            margin: 1em 0;
                            width: 100%;
                        }
                        th, td {
                            border: 1px solid #ddd;
                            padding: 8px;
                            text-align: left;
                        }
                        th {
                            background-color: #f5f5f5;
                            font-weight: 600;
                        }
                        tr:nth-child(even) {
                            background-color: #f9f9f9;
                        }
                        blockquote {
                            border-left: 4px solid #ddd;
                            margin: 1em 0;
                            padding-left: 1em;
                            color: #666;
                        }
                        code {
                            background-color: #f5f5f5;
                            padding: 2px 4px;
                            border-radius: 3px;
                            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
                            font-size: 0.9em;
                        }
                        pre {
                            background-color: #f5f5f5;
                            padding: 1em;
                            border-radius: 4px;
                            overflow-x: auto;
                            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
                        }
                        img {
                            max-width: 100%;
                            height: auto;
                        }
                        .page-break {
                            page-break-before: always;
                        }
                    </style>
                </head>
                <body>
                    ${htmlContent}
                </body>
                </html>
            `;

      setDocxHtml(htmlContent);
      setContent(htmlContent);
    } catch (error) {
      console.error("Error processing DOCX file:", error);
      if (file.preview && typeof file.preview === "string") {
        setContent(file.preview);
      } else {
        throw new Error(`Failed to process DOCX file: ${error.message}`);
      }
    }
  };

  const loadEmlFile = async () => {
    try {
      let emlContent = "";

      // Get EML content from various sources
      if (file.originalFile && file.originalFile instanceof File) {
        emlContent = await file.originalFile.text();
      } else if (file.objectUrl) {
        const response = await fetch(file.objectUrl);
        emlContent = await response.text();
      } else if (file.preview && typeof file.preview === "string") {
        emlContent = file.preview;
      } else {
        throw new Error("No EML data available for preview");
      }

      // Parse EML content
      const parsedData = parseEmlContent(emlContent);
      setEmlData(parsedData);

      // Set content as formatted text for fallback
      setContent(formatEmlAsText(parsedData));
    } catch (error) {
      console.error("Error processing EML file:", error);
      if (file.preview && typeof file.preview === "string") {
        setContent(file.preview);
      } else {
        throw new Error(`Failed to process EML file: ${error.message}`);
      }
    }
  };

  const parseEmlContent = (emlContent) => {
    const parsed = {
      headers: {},
      subject: "",
      from: "",
      to: "",
      cc: "",
      bcc: "",
      date: "",
      body: "",
      attachments: [],
      isHtml: false,
    };

    try {
      // Split headers and body
      const lines = emlContent.split("\n");
      let inHeaders = true;
      let bodyStartIndex = 0;
      let currentHeader = "";

      // Parse headers
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Empty line indicates end of headers
        if (line.trim() === "") {
          inHeaders = false;
          bodyStartIndex = i + 1;
          break;
        }

        // Check for header continuation
        if (line.startsWith(" ") || line.startsWith("\t")) {
          // This is a continuation of the previous header
          if (currentHeader) {
            parsed.headers[currentHeader] += " " + line.trim();
          }
        } else {
          // New header
          const colonIndex = line.indexOf(":");
          if (colonIndex > 0) {
            currentHeader = line.substring(0, colonIndex).trim();
            const value = line.substring(colonIndex + 1).trim();
            parsed.headers[currentHeader] = value;

            // Map common headers
            const lowerHeader = currentHeader.toLowerCase();
            if (lowerHeader === "subject") parsed.subject = value;
            if (lowerHeader === "from") parsed.from = value;
            if (lowerHeader === "to") parsed.to = value;
            if (lowerHeader === "cc") parsed.cc = value;
            if (lowerHeader === "bcc") parsed.bcc = value;
            if (lowerHeader === "date") parsed.date = value;
          }
        }
      }

      // Extract body (everything after headers)
      const bodyContent = lines.slice(bodyStartIndex).join("\n");

      // Try to parse multipart content
      const contentType = parsed.headers["Content-Type"] || "";
      if (contentType.includes("multipart")) {
        // Simple multipart parsing
        const boundaryMatch = contentType.match(/boundary="?([^"\s;]+)"?/);
        if (boundaryMatch) {
          const boundary = boundaryMatch[1];
          const parts = bodyContent.split(`--${boundary}`);

          for (const part of parts) {
            if (part.trim() && !part.includes("--")) {
              const partLines = part.split("\n");
              let partHeadersEnd = 0;
              let partHeaders = {};

              // Parse part headers
              for (let i = 0; i < partLines.length; i++) {
                if (partLines[i].trim() === "") {
                  partHeadersEnd = i;
                  break;
                }
                const colonIndex = partLines[i].indexOf(":");
                if (colonIndex > 0) {
                  const header = partLines[i].substring(0, colonIndex).trim();
                  const value = partLines[i].substring(colonIndex + 1).trim();
                  partHeaders[header] = value;
                }
              }

              const partBody = partLines
                .slice(partHeadersEnd + 1)
                .join("\n")
                .trim();
              const partContentType = partHeaders["Content-Type"] || "";

              if (partContentType.includes("text/plain")) {
                parsed.body = partBody;
              } else if (partContentType.includes("text/html")) {
                parsed.body = partBody;
                parsed.isHtml = true;
              } else if (partContentType.includes("attachment") || partHeaders["Content-Disposition"]?.includes("attachment")) {
                parsed.attachments.push({
                  contentType: partContentType,
                  filename: extractFilenameFromHeaders(partHeaders),
                  size: partBody.length,
                });
              }
            }
          }
        }
      } else {
        // Single part content
        parsed.body = bodyContent;
        parsed.isHtml = contentType.includes("text/html");
      }

      // If no body found, use entire content after headers
      if (!parsed.body && bodyContent) {
        parsed.body = bodyContent;
      }

      return parsed;
    } catch (error) {
      console.error("Error parsing EML:", error);
      // Fallback: return raw content
      return {
        headers: {},
        subject: "Parse Error",
        from: "",
        to: "",
        date: "",
        body: emlContent,
        isHtml: false,
        attachments: [],
      };
    }
  };

  const extractFilenameFromHeaders = (headers) => {
    const contentDisposition = headers["Content-Disposition"] || "";
    const filenameMatch = contentDisposition.match(/filename="?([^"\s;]+)"?/);
    if (filenameMatch) return filenameMatch[1];

    const contentType = headers["Content-Type"] || "";
    const nameMatch = contentType.match(/name="?([^"\s;]+)"?/);
    if (nameMatch) return nameMatch[1];

    return "attachment.bin";
  };

  const formatEmlAsText = (parsedData) => {
    let text = `Subject: ${parsedData.subject}\n`;
    text += `From: ${parsedData.from}\n`;
    text += `To: ${parsedData.to}\n`;
    if (parsedData.cc) text += `Cc: ${parsedData.cc}\n`;
    if (parsedData.bcc) text += `Bcc: ${parsedData.bcc}\n`;
    text += `Date: ${parsedData.date}\n`;
    text += "\n--- Body ---\n\n";
    text += parsedData.body;

    if (parsedData.attachments.length > 0) {
      text += "\n\n--- Attachments ---\n";
      parsedData.attachments.forEach((att, index) => {
        text += `${index + 1}. ${att.filename} (${att.contentType})\n`;
      });
    }

    return text;
  };

  const loadTextFile = () => {
    if (file.preview && typeof file.preview === "string") {
      setContent(file.preview);
    } else {
      setContent("No content available");
    }
  };

  const loadOtherFile = () => {
    if (file.preview && typeof file.preview === "string") {
      setContent(file.preview);
    }
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      setFileUrl(url);
      setError(null);
      setFileType(uploadedFile.name.split(".").pop().toLowerCase());

      const ext = uploadedFile.name.split(".").pop().toLowerCase();
      if (ext === "docx" || ext === "doc") {
        processUploadedDocx(uploadedFile);
      } else if (ext === "eml") {
        processUploadedEml(uploadedFile);
      }
    }
    event.target.value = "";
  };

  const processUploadedDocx = async (file) => {
    setLoading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
      let htmlContent = result.value;

      htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <style>
                        body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
                        h1, h2, h3 { color: #333; }
                        p { margin: 10px 0; }
                        table { border-collapse: collapse; width: 100%; }
                        th, td { border: 1px solid #ddd; padding: 8px; }
                        th { background-color: #f2f2f2; }
                    </style>
                </head>
                <body>${htmlContent}</body>
                </html>
            `;

      setDocxHtml(htmlContent);
      setContent(htmlContent);
    } catch (error) {
      console.error("Error processing uploaded DOCX:", error);
      setError(`Failed to process DOCX: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const processUploadedEml = async (file) => {
    setLoading(true);
    try {
      const emlContent = await file.text();
      const parsedData = parseEmlContent(emlContent);
      setEmlData(parsedData);
      setContent(formatEmlAsText(parsedData));
    } catch (error) {
      console.error("Error processing uploaded EML:", error);
      setError(`Failed to process EML: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = () => {
    if (!file) return;

    try {
      // Handle EML files
      if (fileType === "eml") {
        if (file.originalFile && file.originalFile instanceof File) {
          const url = URL.createObjectURL(file.originalFile);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.filename || "email.eml";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else if (emlData) {
          const emlText = formatEmlAsText(emlData);
          const blob = new Blob([emlText], { type: "message/rfc822" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = (file.filename || "email").replace(/\.\w+$/, "") + ".eml";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      }
      // Handle DOCX files
      else if (fileType === "docx" || fileType === "doc") {
        if (file.originalFile && file.originalFile instanceof File) {
          const url = URL.createObjectURL(file.originalFile);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.filename || "document.docx";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else if (docxHtml) {
          const blob = new Blob([docxHtml], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = (file.filename || "document").replace(/\.[^/.]+$/, "") + ".html";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else {
          const blob = new Blob([content || ""], { type: "text/plain" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = (file.filename || "file").replace(/\.[^/.]+$/, "") + "_extracted.txt";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      }
      // Handle other files
      else {
        let url = fileUrl;

        if (!url && file.originalFile) {
          url = URL.createObjectURL(file.originalFile);
        }

        if (url) {
          const a = document.createElement("a");
          a.href = url;
          a.download = file.filename || "document";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          if (url !== fileUrl) {
            URL.revokeObjectURL(url);
          }
        } else {
          const blob = new Blob([content || ""], { type: "text/plain" });
          const fallbackUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = fallbackUrl;
          a.download = (file.filename || "file").replace(/\.[^/.]+$/, "") + "_extracted.txt";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(fallbackUrl);
        }
      }
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const renderPDFViewer = () => {
    if (!fileUrl) return null;

    return <embed src={fileUrl} type="application/pdf" className="w-full h-full" title="PDF Preview" />;
  };

  const renderDocxViewer = () => {
    if (loading) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tone-blue mx-auto"></div>
            <p className="mt-3 text-gray-600">Converting DOCX to HTML...</p>
          </div>
        </div>
      );
    }

    if (docxHtml) {
      return (
        <div className="h-full overflow-hidden flex flex-col">
          <div className="flex-1 overflow-auto bg-gray-50">
            <iframe srcDoc={docxHtml} title="DOCX Preview" className="w-full h-full border-0" sandbox="allow-same-origin" />
          </div>
        </div>
      );
    }

    return (
      <div className="h-full overflow-hidden flex flex-col">
        <div className="p-3 border-b bg-tone-blue-soft">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-tone-blue-soft rounded flex items-center justify-center">
                <span className="text-tone-blue font-bold">DOC</span>
              </div>
              <span className="text-sm text-gray-600">Microsoft Word Document (Text Preview)</span>
            </div>
            <ButtonShadcn size="sm" onClick={downloadFile}>
              Download
            </ButtonShadcn>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-50 p-6">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <div className="font-sans text-gray-800 leading-relaxed whitespace-pre-wrap">{content}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmlViewer = () => {
    if (!emlData) {
      return renderTextFile();
    }

    return (
      <div className="h-full overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto bg-gray-50 p-6">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
            {/* Email Header */}
            <div className="border-b p-6 bg-gray-50">
              <div className="space-y-3 text-sm">
                <div className="flex">
                  <span className="w-16 font-medium text-gray-600">From:</span>
                  <span className="flex-1 text-gray-900">{emlData.from}</span>
                </div>
                <div className="flex">
                  <span className="w-16 font-medium text-gray-600">To:</span>
                  <span className="flex-1 text-gray-900">{emlData.to}</span>
                </div>
                {emlData.cc && (
                  <div className="flex">
                    <span className="w-16 font-medium text-gray-600">Cc:</span>
                    <span className="flex-1 text-gray-900">{emlData.cc}</span>
                  </div>
                )}
                {emlData.bcc && (
                  <div className="flex">
                    <span className="w-16 font-medium text-gray-600">Bcc:</span>
                    <span className="flex-1 text-gray-900">{emlData.bcc}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="w-16 font-medium text-gray-600">Date:</span>
                  <span className="flex-1 text-gray-900">{emlData.date}</span>
                </div>
              </div>
            </div>

            {/* Email Body */}
            <div className="p-6">
              {emlData.isHtml ? (
                // emlData.body comes from a hand-parsed, uploaded .eml file — attacker-controlled
                // content, sanitized here (not at parse time) so it's enforced at the one place
                // it reaches the DOM regardless of how many code paths can set it.
                <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emlData.body) }} />
              ) : (
                <pre className="whitespace-pre-wrap text-gray-800 font-sans text-sm leading-6">{emlData.body}</pre>
              )}
            </div>

            {/* Attachments */}
            {emlData.attachments && emlData.attachments.length > 0 && (
              <div className="border-t p-6 bg-gray-50">
                <h3 className="font-medium text-gray-900 mb-3">Attachments ({emlData.attachments.length})</h3>
                <div className="space-y-2">
                  {emlData.attachments.map((attachment, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                          <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{attachment.filename}</div>
                          <div className="text-xs text-gray-500">{attachment.contentType}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">{attachment.size ? `${Math.round(attachment.size / 1024)} KB` : "Size unknown"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTextFile = () => {
    return (
      <div className="h-full overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto bg-gray-50 p-6">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-6">
            <pre className="whitespace-pre-wrap text-gray-800 font-mono text-sm leading-6">{content}</pre>
          </div>
        </div>
      </div>
    );
  };

  const renderOtherFile = () => {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <div className="space-y-4">
            {content && (
              <div className="mt-4">
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-sm text-gray-600 whitespace-pre-wrap">
                    {content.substring(0, 1000)}
                    {content.length > 1000 ? "..." : ""}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading && !docxHtml && !emlData) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tone-blue mx-auto"></div>
            <p className="mt-3 text-gray-600">Loading document...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Cannot Display File</h3>
            <p className="text-gray-600 mb-4">{error}</p>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">Upload file directly:</p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors cursor-pointer">
                <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc,.eml,.txt" onChange={handleFileUpload} className="hidden" />
                <ButtonShadcn onClick={() => fileInputRef.current?.click()} className="w-full bg-tone-blue-soft text-tone-blue hover:bg-tone-blue-soft/70">
                  Choose File
                </ButtonShadcn>
                <p className="text-xs text-gray-500 mt-2">Select a file from your device</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Render based on file type
    switch (fileType) {
      case "pdf":
        return renderPDFViewer();

      case "docx":
      case "doc":
        return renderDocxViewer();

      case "eml":
        return renderEmlViewer();

      case "txt":
      case "json":
      case "js":
      case "jsx":
      case "ts":
      case "tsx":
      case "css":
      case "html":
      case "xml":
      case "md":
        return renderTextFile();

      default:
        return renderOtherFile();
    }
  };

  const getFileIcon = () => {
    switch (fileType) {
      case "pdf":
        return (
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
            <span className="text-red-500 font-bold text-xs tracking-wide">PDF</span>
          </div>
        );
      case "docx":
      case "doc":
        return (
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <span className="text-blue-600 font-bold text-xs tracking-wide">DOC</span>
          </div>
        );
      case "eml":
        return (
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
            <span className="text-gray-600 font-bold text-xs">{fileType.toUpperCase()}</span>
          </div>
        );
    }
  };

  const getFileTypeName = () => {
    switch (fileType) {
      case "pdf":
        return "PDF Document";
      case "docx":
        return "Word Document";
      case "doc":
        return "Word Document";
      case "eml":
        return "Email Message";
      case "txt":
        return "Text File";
      case "json":
        return "JSON File";
      case "js":
        return "JavaScript File";
      case "jsx":
        return "React JSX File";
      case "ts":
        return "TypeScript File";
      case "tsx":
        return "React TypeScript File";
      case "css":
        return "CSS File";
      case "html":
        return "HTML File";
      case "xml":
        return "XML File";
      case "md":
        return "Markdown File";
      default:
        return `${fileType.toUpperCase()} File`;
    }
  };

  if (!isOpen || !file) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {getFileIcon()}
            <div>
              <h3 className="text-base font-semibold text-gray-900 leading-tight">{file.filename}</h3>
              {file.size && <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>}
            </div>
          </div>

          <ButtonShadcn variant="ghost" size="icon" onClick={onClose} className="rounded-full" title="Close preview" aria-label="Close preview">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ButtonShadcn>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">{renderContent()}</div>
      </div>
    </div>,
    document.body,
  );
};

export default PreviewModal;
